import { FC, useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  ColorOptionLabel,
  MiDetailFields,
  MiModalConfirm,
} from "@miplatform/components";
import {
  CONFIGURABLE_COLORS,
  ENTITY_NAMES,
  INPUT_TYPES,
  ORDER_TYPES_OPTIONS,
  PLATFORM_MODULES_OPTIONS,
  VERIFICATION_REQUEST_POLICY_OPTIONS,
} from "@miplatform/constants";
import { PlatformModules } from "@miplatform/types";
import { mdiAlertOutline } from "@mdi/js";
import { useTrades } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { ConfigAccordionHeader } from "../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import {
  IEmailTemplateData,
  IOrderTemplateForm,
} from "../ConfigOrderTemplates.types";

export const OrderDetailsTabView: FC<OrderDetailsTabViewProps> = ({
  form,
  extraTabProps: { isReadOnly, emailForm } = {},
}: OrderDetailsTabViewProps) => {
  const { control, setValue, watch } = form;
  const { setValue: emailSetValue, watch: emailWatch } =
    emailForm as UseFormReturn<IEmailTemplateData>;
  const theme = useTheme();
  const { getTrades, tradeOptions: trades } = useTrades();

  const { module } = watch();
  const { emailTemplates } = emailWatch();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [moduleValue, setModuleValue] = useState("");

  useEffect(() => {
    getTrades();
  }, []);

  const handleCloseModal = () => {
    setShowConfirmModal(false);
    setModuleValue("");
  };

  const handleConfirmModal = () => {
    setValue("module", moduleValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
    emailSetValue("emailTemplates", [], {
      shouldDirty: true,
      shouldValidate: true,
    });
    handleCloseModal();
  };

  const orderDetailFields = [
    {
      label: `${ENTITY_NAMES.ORDER_TEMPLATE} Name`,
      name: "name",
      formControl: control,
      required: true,
      inputProps: { maxLength: 255 },
      disabled: isReadOnly,
    },
    {
      label: "Short Name",
      name: "shortName",
      formControl: control,
      required: true,
      maxLength: 8,
      disabled: isReadOnly,
    },
    {
      label: "Abbreviation",
      name: "abbreviation",
      formControl: control,
      setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue("abbreviation", e.target.value.toUpperCase(), {
          shouldDirty: true,
          shouldValidate: true,
        });
      },
      required: true,
      maxLength: 3,
      disabled: isReadOnly,
    },
    {
      label: "Order Code Prefix",
      name: "code",
      formControl: control,
      required: true,
      disabled: isReadOnly,
    },
    {
      label: "Color",
      name: "color",
      formControl: control,
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      options: CONFIGURABLE_COLORS.map(({ color, label }) => ({
        label: <ColorOptionLabel color={color} label={label} />,
        value: color,
      })),
      disabled: isReadOnly,
    },
    {
      label: "Description",
      name: "description",
      formControl: control,
      disabled: isReadOnly,
    },
  ];

  const templateClassificationField = [
    {
      label: "Module",
      name: "module",
      formControl: control,
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      options: PLATFORM_MODULES_OPTIONS.map((module) => ({
        label: module.label,
        value: module.value,
      })),
      disabled: isReadOnly,
      setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
        if (module && emailTemplates.some((template) => template.emailType)) {
          setModuleValue(e.target.value);
          setShowConfirmModal(true);
        } else {
          setValue("module", e.target.value, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
        setValue("hasCameraLine", false, {
          shouldDirty: true,
          shouldValidate: true,
        });
      },
    },
    {
      label: "Home Phase",
      name: "homePhases",
      formControl: control,
      required: true,
      fieldType: INPUT_TYPES.MULTI_SELECT,
      options: ORDER_TYPES_OPTIONS,
      disabled: isReadOnly,
    },
    {
      label: "Trade",
      name: "tradeId",
      formControl: control,
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      options: trades,
      disabled: true, // NOTE: defaults to Plumbing for QxP Parity as a read-only - use isReadOnly later
    },
    {
      label: "Requires Verification",
      name: "verificationRequestPolicy",
      formControl: control,
      fieldType: INPUT_TYPES.SELECT,
      options: VERIFICATION_REQUEST_POLICY_OPTIONS,
      disabled: isReadOnly,
    },
    {
      label: "Has Camera Line",
      name: "hasCameraLine",
      formControl: control,
      fieldType: INPUT_TYPES.SWITCH,
      style: module !== PlatformModules.MiDispatch ? { display: "none" } : {},
      disabled: isReadOnly,
    },
    {
      label: "Requires Customer Signature",
      name: "requiresCustomerSignature",
      formControl: control,
      fieldType: INPUT_TYPES.SWITCH,
      disabled: isReadOnly,
    },
  ];

  return (
    <>
      <ConfigAccordionHeader title="Order Details">
        <MiDetailFields detailFields={orderDetailFields} />
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title="Type Classification">
        <MiDetailFields detailFields={templateClassificationField} />
      </ConfigAccordionHeader>
      <MiModalConfirm
        open={showConfirmModal}
        title="Warning"
        description="Changing the module will remove all existing email templates for this order template. Are you sure you want to continue?"
        handlePositive={handleConfirmModal}
        handleNegative={handleCloseModal}
        buttonPositiveColor="error"
        titleIcon={{ path: mdiAlertOutline, color: theme.palette.error.main }}
      />
    </>
  );
};

interface OrderDetailsTabViewProps {
  form: UseFormReturn<IOrderTemplateForm>;
  extraTabProps?: {
    isReadOnly?: boolean;
    emailForm?: UseFormReturn<IEmailTemplateData>;
  };
}
